import { Action, Thunk, action, thunk } from "easy-peasy";
import { post, get } from "requests";

export interface DealApplicationStoreModel {
  timestamp: any[];
  activeStep: number;
  subStatus: number;
  isLastStep: boolean;
  isPreApproved: boolean;
  isDeclined: boolean;
  dealId: Number;
  loading: boolean;
  error: string | null;
  recipients: any[];
  clientDealApplicationVersion: string;
  declineReason: string | null;
  skipEmailSending: boolean;
  emailContent: string | null;
  replyOnSameThread: boolean;
  hasActiveSchedules: boolean;
  refinancedDealNumbers: any[];
  isAnalysisLoaded: boolean;
  prevSelectedAnalysisTab: Number;
  selectedAnalysisTab: Number;

  // Actions
  setDealId: Action<DealApplicationStoreModel, number>;
  setTimestamp: Action<DealApplicationStoreModel, any[]>;
  setActiveStep: Action<DealApplicationStoreModel, number>;
  setSubStatus: Action<DealApplicationStoreModel, number>;
  setLoading: Action<DealApplicationStoreModel, boolean>;
  setIsPreApproved: Action<DealApplicationStoreModel, boolean>;
  setIsLastStep: Action<DealApplicationStoreModel, boolean>;
  reInitializeDealApplicationStore: Action<DealApplicationStoreModel>;
  setError: Action<DealApplicationStoreModel, string | null>;
  setRecipients: Action<DealApplicationStoreModel, any[]>;
  setClientDealApplicationVersion: Action<DealApplicationStoreModel, string>;
  setDeclineReason: Action<DealApplicationStoreModel, string | null>;
  setIsDeclined: Action<DealApplicationStoreModel, boolean>;
  setSkipEmailSending: Action<DealApplicationStoreModel, boolean>;
  setEmailContent: Action<DealApplicationStoreModel, any | null>;
  setReplyOnSameThread: Action<DealApplicationStoreModel, boolean>;
  setHasActiveSchedules: Action<DealApplicationStoreModel, boolean>;
  setRefinancedDealNumbers: Action<DealApplicationStoreModel, any>;
  setIsAnalysisLoaded: Action<DealApplicationStoreModel, boolean>;
  setSelectedTab: Action<DealApplicationStoreModel, number> ;


  // Thunks
  fetchTimestamp: Thunk<DealApplicationStoreModel, string>;
  updateStatusAndSubStatus: Thunk<
    DealApplicationStoreModel,
    {
      dealApplicationId: string;
      status: string;
      subStatus: string;
      externalEmails: string;
      documents: any;
      recipients: any;
      isPreApproved: boolean;
      emailDetails: any;
      isSkip: boolean;
      offersToSend: any[];
      callback: Function;
    }
  >;
  updateDealApplicationNo: Thunk<
    DealApplicationStoreModel,
    { dealApplicationId: Number; callback?: Function }
  >;
  fetchDealIdByApplicationId: Thunk<
    DealApplicationStoreModel,
    { dealApplicationId: Number; callback?: Function }
  >;
  checkIfActiveSchedules: Thunk<DealApplicationStoreModel, { dealApplicationId: Number }>;
  updateEndDateForActiveSchedulesForRefinancedDeals: Thunk<
    DealApplicationStoreModel,
    { dealApplicationId: Number }
  >;
  validateRequiredStepInfo: Thunk<
    DealApplicationStoreModel,
    { dealApplicationId: Number; merchantInfo: any; requiredInfo: any; offerDetails: any }
  >;
}

const DealApplicationStore: DealApplicationStoreModel = {
  timestamp: [],
  activeStep: -1,
  subStatus: -1,
  isLastStep: false,
  isPreApproved: false,
  isDeclined: false,
  loading: false,
  error: null,
  recipients: [],
  clientDealApplicationVersion: "",
  declineReason: null,
  skipEmailSending: false,
  emailContent: null,
  replyOnSameThread: false,
  dealId: null,
  hasActiveSchedules: false,
  isAnalysisLoaded: false,
  selectedAnalysisTab: 0,
  prevSelectedAnalysisTab: 0,

  refinancedDealNumbers: [],

  setSelectedTab: action((state, selectedTab) => {
    if (selectedTab === 1) {
      state.prevSelectedAnalysisTab = state.selectedAnalysisTab;
    }
    state.selectedAnalysisTab = selectedTab;
  }),

  setTimestamp: action((state, timestamp) => {
    state.timestamp = timestamp;
  }),

  setActiveStep: action((state, activeStep) => {
    state.activeStep = activeStep;
  }),

  setIsAnalysisLoaded: action((state, isAnalysisLoaded) => {
    state.isAnalysisLoaded = isAnalysisLoaded;
  }),

  setSubStatus: action((state, subStatus) => {
    state.subStatus = subStatus;
  }),

  setLoading: action((state, loading) => {
    state.loading = loading;
  }),

  setError: action((state, error) => {
    state.error = error;
  }),

  setIsLastStep: action((state, value) => {
    state.isLastStep = value;
  }),

  setIsPreApproved: action((state, value) => {
    state.isPreApproved = value;
  }),

  setRecipients: action((state, recipients) => {
    state.recipients = recipients;
  }),

  setClientDealApplicationVersion: action((state, version) => {
    state.clientDealApplicationVersion = version;
  }),

  setDeclineReason: action((state, reason) => {
    state.declineReason = reason;
  }),

  setSkipEmailSending: action((state, value) => {
    state.skipEmailSending = value;
  }),

  setEmailContent: action((state, content) => {
    state.emailContent = content;
  }),

  setReplyOnSameThread: action((state, value) => {
    state.replyOnSameThread = value;
  }),

  setIsDeclined: action((state, value) => {
    state.isDeclined = value;
  }),

  setDealId: action((state, value) => {
    state.dealId = value;
  }),

  setHasActiveSchedules: action((state, value) => {
    state.hasActiveSchedules = value;
  }),

  setRefinancedDealNumbers: action((state, value) => {
    state.refinancedDealNumbers = value;
  }),
  reInitializeDealApplicationStore: action((state) => {
    state.timestamp = [];
    state.activeStep = -1;
    state.subStatus = -1;
    state.isLastStep = false;
    state.isPreApproved = false;
    state.isDeclined = false;
    state.loading = false;
    state.error = null;
    state.recipients = [];
    state.clientDealApplicationVersion = "";
    state.declineReason = null;
    state.skipEmailSending = false;
    state.emailContent = null;
    state.replyOnSameThread = false;
    state.dealId = null;
    state.hasActiveSchedules = false;
    state.refinancedDealNumbers = [];
    state.isAnalysisLoaded = false;
    state.selectedAnalysisTab = 0;
    state.prevSelectedAnalysisTab = 0;
  }),

  fetchTimestamp: thunk(async (actions, id) => {
    actions.setLoading(true);
    actions.setError(null);

    try {
      const response = await post("steps/getTimestampByDealApplicationId", { id });
      actions.setTimestamp(response?.result);
      const activeStep = response?.result?.length;
      actions.setActiveStep(activeStep);
      actions.setSubStatus(response.result.length >= 0 ? response.result.length - 1 : -1);
    } catch (error: any) {
      actions.setError(error.message);
    } finally {
      actions.setLoading(false);
    }
  }),

  updateStatusAndSubStatus: thunk(
    async (
      actions,
      {
        dealApplicationId, status, subStatus, externalEmails, documents, recipients, isPreApproved, emailDetails, isSkip, offersToSend, callback,
      },
      { getState }
    ) => {
      actions.setLoading(true);
      const APPLICATION_STATUS = "approved";

      const state = getState();
      recipients = isSkip ? [] : recipients;

      try {
        const responseData = await post("dealApplication/updateStatusAndSubStatus", {
          dealApplicationId: dealApplicationId,
          status: status,
          subStatus: subStatus,
          externalEmails: externalEmails ? externalEmails : "",
          documents: documents ? documents : null,
          declineReason: subStatus === "Declined" && state.declineReason ? state.declineReason : null,
          recipients: isSkip ? null : recipients,
          preApproved: isPreApproved,
          emailDetails: isSkip ? null : emailDetails,
          replyOnSameThread: isSkip ? null : state.replyOnSameThread,
          offersToSend: isSkip ? null : offersToSend,
        });

        if (responseData.version) {
          actions.setClientDealApplicationVersion(responseData.version);
        }

        if (responseData?.syndicationPercentageIssue &&
          responseData?.syndicationPercentageIssue === true) {
          return;
        }

        const newSubStatus = responseData.subStatus;
        const newStatus = responseData.status;

        actions.setSubStatus(newSubStatus);
        actions.setActiveStep(responseData.subStatus);

        actions.setError(null);

        if (!state.isLastStep) {
          callback();
        }
      } catch (err: any) {
        actions.setError(err.message);
      } finally {
        actions.setLoading(false);
      }
    }
  ),

  updateDealApplicationNo: thunk(async (actions, { dealApplicationId, callback }, { getState }) => {
    const state = getState();
    if (!dealApplicationId) {
      console.error("Deal Application ID is missing.");
      return;
    }
    try {
      const res = await get(`updateDealApplicationNo/${dealApplicationId}`);
      callback(res);
    } catch (error: any) {
      console.error("Failed to update deal application number:", error);
    }
  }),

  fetchDealIdByApplicationId: thunk(async (actions, { dealApplicationId }, { getState }) => {
    if (!dealApplicationId) {
      console.error("Deal Application ID is missing.");
      return;
    }
    try {
      const res = await get(
        `/deal/search/findByDealApplicationId?dealApplicationId=${dealApplicationId}`
      );
      actions.setDealId(res.id);
    } catch (error: any) {
      console.error("Failed to update deal application number:", error);
    }
  }),

  checkIfActiveSchedules: thunk(async (actions, { dealApplicationId }) => {
    if (!dealApplicationId) {
      console.error("Deal Application ID is missing.");
      return false;
    }
    try {
      actions.setLoading(true);
      const response: any = await get(
        `checkActiveSchedulesForRefinancedDeals/${dealApplicationId}`
      );
      const refinancedDeals = response?.dealNumbers;
      actions.setRefinancedDealNumbers(refinancedDeals);
      actions.setHasActiveSchedules(response?.hasActiveSchedules);
      actions.setLoading(false);
      return response?.hasActiveSchedules;
    } catch (error: any) {
      actions.setHasActiveSchedules(false);
      console.error("Failed to Check active schedules:", error);
      actions.setLoading(false);
      return false;
    }
  }),

  updateEndDateForActiveSchedulesForRefinancedDeals: thunk(
    async (actions, { dealApplicationId }) => {
      await get(`updateEndDateForActiveSchedulesOfRefinancedDeals/${dealApplicationId}`);
      actions.setHasActiveSchedules(false);
      actions.setRefinancedDealNumbers([]);
    }
  ),

  validateRequiredStepInfo: thunk(
    async (actions, { merchantInfo, requiredInfo, offerDetails }, { getState }) => {
      merchantInfo = !merchantInfo ? {} : merchantInfo;
      let errorData = [];
      let status = true;
      let newRequiredInfo = [];

      // User error message mapping
      const userErrorMessage: any = {
        businessLegalName: "Business Legal Name",
        federalTaxId: "Federal Tax Id",
        physicalAddress: "Physical Address",
        "physicalAddress.state": "State",
        users: "At least one owner is required",
      };

      const fieldsToCheck = [
        "businessLegalName",
        "federalTaxId",
        "physicalAddress",
        "physicalAddress.state",
        "users",
      ];

      for (let key in requiredInfo) {
        const requiredKey = requiredInfo[key];

        if (!fieldsToCheck.includes(requiredKey)) {
          newRequiredInfo.push(requiredKey);
          continue;
        }

        if (requiredKey.includes(".")) {
          let keys = requiredKey.split(".");
          if (!merchantInfo[keys[0]]) {
            status = false;
            errorData.push(userErrorMessage[requiredKey]);
          } else if (Array.isArray(merchantInfo[keys[0]][keys[1]]) &&
            merchantInfo[keys[0]][keys[1]].length === 0) {
            status = false;
            errorData.push(userErrorMessage[requiredKey]);
          } else if (!merchantInfo[keys[0]][keys[1]]) {
            status = false;
            errorData.push(userErrorMessage[requiredKey]);
          }
        } else {
          if (Array.isArray(merchantInfo[requiredKey])) {
            if (merchantInfo[requiredKey].length === 0 ||
              (merchantInfo[requiredKey].length === 1 &&
                Object.keys(merchantInfo[requiredKey][0]).length === 0)) {
              status = false;
              errorData.push(userErrorMessage[requiredKey]);
            }
          } else if (!merchantInfo[requiredKey]) {
            status = false;
            errorData.push(userErrorMessage[requiredKey]);
          }
        }
      }

      //Validation for offerInfo
      if (offerDetails) {
        const fieldsToCheck = [
          "fundedAmount",
          "paybackFactor",
          "contractTerms.funder",
          "purchaseAmount",
          "contractTerms.commissionAmount",
        ];
        const userErrorMessage: any = {
          fundedAmount: "Accepted Offer (Funded Amount)",
          paybackFactor: "Accepted  Offer (Payback Factor)",
          "contractTerms.funder": "Funder",
          purchaseAmount: "Accepted Offer (Purchase Amount)",
          "contractTerms.commissionAmount": "Commission Amount",
        };

        newRequiredInfo = [];

        for (let key in requiredInfo) {
          const requiredKey = requiredInfo[key];
          if (!fieldsToCheck.includes(requiredKey)) {
            newRequiredInfo.push(requiredKey);
            continue;
          }

          if (requiredKey.includes(".")) {
            let keys = requiredKey.split(".");
            if (!offerDetails[keys[0]]) {
              status = false;
              errorData.push(userErrorMessage[requiredKey]);
            } else if (Array.isArray(offerDetails[keys[0]][keys[1]]) &&
              offerDetails[keys[0]][keys[1]].length === 0) {
              status = false;
              errorData.push(userErrorMessage[requiredKey]);
            } else if (!offerDetails[keys[0]][keys[1]]) {
              status = false;
              errorData.push(userErrorMessage[requiredKey]);
            } else if (offerDetails[keys[0]][keys[1]] === 0) {
              status = false;
              errorData.push(userErrorMessage[requiredKey]);
            }
          } else {
            if (Array.isArray(offerDetails[requiredKey])) {
              if (offerDetails[requiredKey].length === 0 ||
                (offerDetails[requiredKey].length === 1 &&
                  Object.keys(offerDetails[requiredKey][0]).length === 0)) {
                status = false;
                errorData.push(userErrorMessage[requiredKey]);
              } else if (!offerDetails[requiredKey]) {
                status = false;
                errorData.push(userErrorMessage[requiredKey]);
              }
            } else {
              if (!offerDetails[requiredKey]) {
                status = false;
                errorData.push(userErrorMessage[requiredKey]);
              }
            }
          }
        }
      }

      return { valid: status, error: errorData };
    }
  ),

};

export default DealApplicationStore;
